import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { DropzoneDirective, DropzoneConfig } from '../../shared/dropzone/dropzone.directive';
import { UploadService } from '../../services/upload/upload.service';
import { CommonModule } from '@angular/common';
import { InstrumentRunService } from '../../services/instrument-run/instrument-run.service';
import { ButtonsComponent } from '../buttons/buttons.component';
import { InputComponent } from '../input/input.component';
import { UploadType } from '../../services/upload/upload-type';

@Component({
  selector: 'app-file-upload',
  imports: [DropzoneDirective, CommonModule, ButtonsComponent],
  standalone: true,
  templateUrl: './file-upload.component.html',
  styleUrls: ['./file-upload.component.css'],
})
export class FileUploadComponent implements OnInit {
  @ViewChild(DropzoneDirective) dropzoneDirective?: DropzoneDirective;
  url = ``;
  // Basic upload configuration
  @Input() apiUrl = this.url;
  @Input() acceptedFileTypes = '*';
  @Input() maxFileSize = 5000; // in MB

  // Chunking related inputs
  @Input() useChunking = true;
  @Input() chunkSize = 10 * 1024 * 1024; // 10MB
  @Input() folderPath?: string = '';
  @Input() fileName?: string | null = '';
  @Input() custom_fileName: boolean = false;
  @Input() isFileExist: boolean = false;
  @Input() fileDescription?: string | null;
  // Additional configuration options
  @Input() maxFiles?: number;
  @Input() parallelUploads = 4;
  @Input() createThumbnails = true;
  @Input() addRemoveLinks = true;
  @Input() autoProcessQueue = false;
  @Input() dictDefaultMessage = 'Drop files here or click to upload';
  @Input({ required: true }) featureName!: UploadType;
  @Input() files = [];

  // Custom callbacks
  @Input() onUploadStarted?: (fileIds: string[]) => void;
  @Input() onAllCompleted?: () => void;
  @Output() fileAdded = new EventEmitter<boolean>();
  @Output() uploadSuccess = new EventEmitter<any>();

  // Add the missing properties
  errorMessage: string | null = null; // For displaying error messages
  hasFiles = false; // Track if files are present

  dropzoneConfig!: DropzoneConfig;
  isCreatingSetup = false;
  isDisabled = false;

  constructor(private uploadService: UploadService) {}

  ngOnInit() {
    // Configure Dropzone with all our inputs
    this.dropzoneConfig = {
      url: this.apiUrl,
      acceptedFiles: this.acceptedFileTypes,
      maxFilesize: this.maxFileSize,
      maxFiles: this.maxFiles,
      parallelUploads: this.parallelUploads,
      createImageThumbnails: this.createThumbnails,
      addRemoveLinks: this.addRemoveLinks,
      autoProcessQueue: this.autoProcessQueue,
      dictDefaultMessage: this.dictDefaultMessage,

      // Chunking configuration
      chunking: this.useChunking,
      // forceChunking: this.useChunking,
      chunkSize: this.chunkSize,
      // parallelChunkUploads: true,
      retryChunks: true,
      retryChunksLimit: 3,
    };
  }
  selectFileBtnText = $localize`Select File`;
  btnMatStroked = 'mat-stroked-button';
  btnStyleStroked = 'btn-stroked';
  downloadIcon = 'cloud_upload';
  // Handle file addition
  onFileAdded(file: File) {
    this.isDisabled = true;
    this.hasFiles = true;

    // Clear any previous error
    this.errorMessage = null;

    // Check file format if acceptedFileTypes is specified
    if (this.acceptedFileTypes !== '*' && !this.uploadService.isFileTypeAllowed(file, this.acceptedFileTypes)) {
      this.showError(`File type not allowed: ${file.name}`);
      return;
    }

    // Check file size
    if (file.size > this.maxFileSize * 1024 * 1024) {
      this.showError(`File too large: ${file.name}`);
      return;
    }

    this.fileAdded.emit(true);
  }

  // Add the missing method to handle file rejections
  onFileRejected(event: { file: File; reason: string }) {
    console.error('File rejected:', event.file.name, event.reason);
    this.showError(event.reason);
  }

  // Track upload progress
  onUploadProgress(event: { file: File; progress: number }) {
    //Might be used in future for tracking progress
  }

  // Handle successful upload
  onUploadSuccess(event: { file: File; response: any }) {
    // Call the custom callback if provided
    if (this.onAllCompleted && this.isAllUploadsComplete()) {
      this.onAllCompleted();
    }
  }

  // Handle upload errors
  onUploadError(event: { file: File; error: any }) {
    console.error(`Upload failed for ${event.file.name}`, event.error);
    this.showError(`Upload failed for ${event.file.name}: ${event.error.message || 'Unknown error'}`);
  }

  onDuplicateUploadError(error: string) {
    console.error(error);
    this.showError(error);
    setTimeout(() => {
      this.errorMessage = null;
    }, 3000);
  }

  // Handle file removal
  onFileRemoved(file: File) {
    // Update hasFiles status by checking if any files remain
    const dropzone = this.dropzoneDirective?.getDropzoneInstance();
    if (dropzone) {
      this.hasFiles = dropzone.files.length > 0;
    }
  }
  onComplete($event: any) {
    this.uploadSuccess.emit($event);
  }

  // Public methods that can be called from parent components

  // Add files programmatically
  addFiles(files: File[]) {
    if (this.dropzoneDirective) {
      this.dropzoneDirective.addFiles(files);
      this.hasFiles = true;
    }
  }

  // Reset the uploader
  reset() {
    if (this.dropzoneDirective) {
      this.dropzoneDirective.resetDropzone();
      this.hasFiles = false;
      this.errorMessage = null;
    }
  }

  // Cancel all uploads
  cancelAll() {
    if (this.dropzoneDirective) {
      this.dropzoneDirective.cancelAll();
    }
  }

  // File selection via button click
  browseFiles() {
    // Trigger click on the file input
    const fileInput = document.querySelector('.dz-hidden-input');
    if (fileInput) {
      (fileInput as HTMLElement).click();
    }
  }

  // Helper methods

  private isAllUploadsComplete(): boolean {
    // Use the dropzone directive to check if all uploads are complete
    const dropzone = this.dropzoneDirective?.getDropzoneInstance();
    if (dropzone) {
      return dropzone.getUploadingFiles().length === 0 && dropzone.getQueuedFiles().length === 0;
    }
    return true;
  }

  private showError(message: string) {
    this.errorMessage = message;
    console.error(message);
    // You can also use an error notification service here if available
    // e.g. this.notificationService.showError(message);
  }
}
